/**
 * The rows-side `Backend` over the server's REST routes: the backend a
 * `@tento/lona/sheets` sheet reads and writes the live server through.
 * `@tento/lona`'s client composes one over its own API
 * (`client.httpBackend()` hands a host a fresh one); `SyncHttpBackend` is
 * the `@tento/lona` sync layer's twin.
 *
 * - Reads: `fetchSheet` (`GET /api/sheets/{sheetKey}`; a 404 is `null`),
 *   `fetchCells` (`GET /api/rows/{rowKey}/cells`) and `fetchCellsBatch`
 *   (`POST /api/sheets/{sheetKey}/cells/batch`; a 429 throws
 *   `BatchFetchThrottledError`).
 * - Checked writes: `addRow`, `updateRow`, `deleteRow`,
 *   `writeTimepointCell` and `clearTimepointCell` await the server, and a
 *   refusal or no answer throws `RowsHttpBackendRequestError`.
 * - Handed-off writes: `writeCell` / `clearCell` go to the service-worker
 *   request queue. Built with the client's verdict channel
 *   (`RowsHttpBackend.CellVerdicts`, `client.httpBackend()`), each resolves on
 *   the verdict of the execution that carried it and a refusal throws
 *   `RowsHttpBackendRequestError` (step `write-cell` / `clear-cell`), reported
 *   to the host once; without one it resolves once handed off.
 * - Subscriptions: `onRowChange` / `onCellChange` deliver nothing; live
 *   changes reach a client through its sync layer, not through this
 *   backend.
 * - Not sent: `expectedEtag` (no `If-Match` header) and `addRow`'s
 *   `position` (the caller reorders). `updateRow` sends the fields
 *   `PATCH /api/rows/{rowKey}` takes (`label`, `formula`, `lookupKey`,
 *   `timeScale`, `heightPx`, `attributes`); a patch that carries any other
 *   field, or a time scale the route does not name, is refused before
 *   anything is sent (`RowsHttpBackendRequestError`, outcome `unaddressable`).
 */

import type { Option } from "../core/option.ts";
import { Json } from "../core/json.ts";
import { type DateTime, NaiveDate, SemanticTime, type Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import type { Result } from "@tento-core/result";
import { RequestTransportError, type ResponseError, type TypedApi } from "@tento-core/typed-api";
import { Uuid as UuidFormat } from "@tento-core/uuid";

import {
  type Backend,
  type BatchRowCells,
  BatchFetchThrottledError,
  type CellChange,
  type Etag,
  type FetchedCells,
  type RowChange,
  type SurfaceKind,
  type Unsubscribe,
} from "./rows-backend.ts";
import { CellEntry, type CellValue, cellValueWire } from "../core/dtype.ts";
import type { RawRow, RawSheet } from "../wire/raw-sheet-wire.ts";
import { SdkRequestError, requestRejection } from "../core/errors.ts";
import { RowId } from "../ids/row-id.ts";
import { SheetsNetwork } from "../wire/rows-network.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import { CellId, RowUuid, SheetUuid } from "../ids/ids.ts";
import { SheetId } from "../ids/sheet-id.ts";

// SourceRow already coalesces rapid cell edits before it calls the backend.
// Keep this queue at 0ms so the service worker is only a durable execution
// handoff, not a second debounce layer that delays blur/pagehide persistence.
const CELL_WRITE_DEBOUNCE_MS = 0;

function cellWriteDebounceKey(
  rowId: RowId,
  date: NaiveDate.Partial,
  id: Option<string>,
): string {
  return `cell:${rowId.toString()}:${date.toString()}:${id ?? ""}`;
}

/**
 * The rows `Backend` over the server's REST routes, built on a `TypedApi` of
 * the rows routes (`SheetsNetwork.Routes`).
 *
 * `fetchSheet`, `writeTimepointCell`, `clearTimepointCell`, `addRow`,
 * `updateRow` and `deleteRow` are checked: a
 * request the server rejects or does not answer throws
 * `RowsHttpBackendRequestError` (a 404 on `fetchSheet` resolves `null`);
 * `updateRow` refuses a field its route does not take before sending it
 * (outcome `unaddressable`). `writeCell` / `clearCell` hand off to the service-worker debounce and,
 * with a verdict channel (`verdicts`), resolve on the verdict of the execution that carried them: a
 * refusal throws `RowsHttpBackendRequestError` (so the writer's ledger takes its paint back) after
 * `verdicts.refused` reported it once. With no channel they resolve once handed off.
 * The per-row cell reads (`fetchCells`, `fetchCellsByKey`) are tolerant: a non-2xx answer reads
 * as no cells; their request's own failure throws `SdkRequestError` (boundary `cells`), outcome
 * `unanswered` for a rejected fetch, `malformed` for a body that did not decode. The sheet-wide
 * bulk read (`fetchSheetCells`) is required: the accessor admits its range as covered on success,
 * so a non-2xx answer throws too, outcome `refused` with the status.
 */
export class RowsHttpBackend implements Backend {
  readonly #api: TypedApi<SheetsNetwork.Routes>;
  #verdicts: Option<RowsHttpBackend.CellVerdicts> = null;
  constructor(api: TypedApi<SheetsNetwork.Routes>) {
    this.#api = api;
  }

  /**
   * A backend over `api` whose handed-off cell writes (`writeCell`,
   * `clearCell`) resolve on their verdict through `verdicts` — what
   * `client.httpBackend()` builds.
   */
  static withVerdicts(api: TypedApi<SheetsNetwork.Routes>, verdicts: RowsHttpBackend.CellVerdicts): RowsHttpBackend {
    const backend = new RowsHttpBackend(api);
    backend.#verdicts = verdicts;
    return backend;
  }

  // ── Reads ───────────────────────────────────────────────────────────

  /** The sheet as a `RawSheet`; `null` on a 404, `RowsHttpBackendRequestError` otherwise. */
  async fetchSheet(sheetKey: SheetNetworkKey): Promise<Option<RawSheet>> {
    const result = await sendChecked(
      "get",
      "GET",
      "/api/sheets/{sheetKey}",
      () => this.#api.getResult("/api/sheets/{sheetKey}", { path: { sheetKey } }),
      true,
    );
    return result != null ? toRawSheet(result) : null;
  }

  fetchCells(
    rowId: RowId,
    range: NaiveDate.Range,
  ): Promise<FetchedCells> {
    return this.#fetchCellsAt(RowNetworkKey.forRead(rowId), range);
  }

  fetchCellsByKey(
    key: RowNetworkKey,
    range: NaiveDate.Range,
  ): Promise<FetchedCells> {
    return this.#fetchCellsAt(RowNetworkKey.forRead(key), range);
  }

  /** `GET /api/rows/{rowKey}/cells` over `range`, split into the date and datetime lanes. */
  async #fetchCellsAt(
    rowKey: RowNetworkKey,
    range: NaiveDate.Range,
  ): Promise<FetchedCells> {
    const query = { start: range.start, end: range.end, mock_sync_delay: null, booking_id: null };
    const wire = await this.#api.get("/api/rows/{rowKey}/cells", { path: { rowKey }, query }).catch(
      (err: unknown): never => {
        throw cellReadRequestError("get-cells", "/api/rows/{rowKey}/cells", err);
      },
    );
    if (!wire) return { cells: [], custom: [] };
    const cells: CellEntry[] = [];
    const custom: CellEntry.Timepoint[] = [];
    for (const cell of wire) {
      // Route by the cell's TIME SHAPE, not `dateKey`: `dateKey`
      // collapses a datetime to its containing day (non-null), which
      // would mis-route sticker / scheduling cells into the standard
      // date lane and drop the time. `partialDate` is null exactly when
      // the wire date carries a time component, so it cleanly separates
      // date-anchored cells from datetime-positioned (custom) ones — the
      // latter keep their `DateTime.Range` verbatim in the custom lane.
      const partial = SemanticTime.partialDate(cell.date);
      if (partial != null) {
        cells.push({
          date: partial,
          value: cell.data ?? null,
          id: cell.id ?? null,
          style: null,
        });
        continue;
      }
      const tp = CellEntry.Timepoint.tryFrom(cell);
      if (tp != null) custom.push(tp);
    }
    return { cells, custom };
  }

  async fetchSheetCells(
    sheetKey: SheetNetworkKey,
    range: NaiveDate.Range,
  ): Promise<SheetsNetwork.SheetRowDataResponse[]> {
    const query = { start: range.start, end: range.end, mock_sync_delay: null, booking_id: null };
    const path = "/api/sheets/{sheetKey}/cells";
    const rows = await SdkRequestError.checkRequest(
      "cells",
      "get-sheet-cells",
      "GET",
      path,
      () => this.#api.getResult(path, { path: { sheetKey }, query }),
    );
    return rows ?? [];
  }

  async fetchCellsBatch(
    sheetKey: SheetNetworkKey,
    rowKeys: readonly RowNetworkKey[],
    range: NaiveDate.Range,
  ): Promise<Map<RowNetworkKey, BatchRowCells>> {
    const result = await this.#api.postResult(
      "/api/sheets/{sheetKey}/cells/batch",
      {
        path: { sheetKey },
        body: {
          rowKeys: [...rowKeys],
          range: { start: range.start, end: range.end },
        },
      },
    ).catch((err) => {
      // Classify only the request's rejected promise, never result matching,
      // delegate handling or decoded cell parsing below.
      // A rejected postResult may instead be a construction or validator bug.
      if (!(err instanceof RequestTransportError)) throw err;
      // A host transport that refused with a typed error keeps its verdict.
      const raw = requestRejection(err);
      if (raw instanceof SdkRequestError) throw raw;
      throw SdkRequestError.rejected(raw, (answer) => new SdkRequestError({
        boundary: "cells", step: "get-cells-batch", method: "POST",
        path: "/api/sheets/{sheetKey}/cells/batch",
        ...SdkRequestError.Answer.fields(answer),
      }));
    });
    // `post` resolved every non-200 to `undefined`, so a 429 read as "not
    // found or no access". Split by status: a 429 is a throttle the caller
    // retries after a pause; anything else keeps the delegate hook `post`
    // ran (e.g. the app's not-logged-in redirect).
    const wire = result.match({
      ok: (body) => body,
      err: (e) => {
        if (e.code === 429) throw new BatchFetchThrottledError(sheetKey, null);
        this.#api.delegate?.onUncaughtError(e, {
          method: "POST",
          route: "/api/sheets/{sheetKey}/cells/batch",
        });
        return null;
      },
    });
    if (wire == null) {
      throw new Error(
        `fetchCellsBatch: sheet "${sheetKey}" not found or no access`,
      );
    }
    const out = new Map<RowNetworkKey, BatchRowCells>();
    for (const row of wire.rows) {
      if (row.status === "ok") {
        const cells: CellEntry[] = [];
        for (const cell of row.cells ?? []) {
          const date = SemanticTime.dateKey(cell.date);
          if (date == null) continue;
          cells.push({
            date,
            value: cell.data ?? null,
            id: cell.id ?? null,
            style: null,
          });
        }
        out.set(row.rowKey, {
          status: "ok",
          rowKey: row.rowKey,
          metadata: row.metadata ?? null,
          cells,
          syncKeys: row.syncKeys ?? null,
        });
      } else {
        out.set(row.rowKey, {
          status: row.status,
          rowKey: row.rowKey,
          error: row.error ?? "<unspecified>",
        });
      }
    }
    return out;
  }

  // ── Subscriptions ───────────────────────────────────────────────────

  onRowChange(_handler: (change: RowChange) => void): Unsubscribe {
    // Delivers nothing: live changes reach a client through its sync
    // layer (`SyncClient`), not through this backend.
    return () => {};
  }

  onCellChange(
    _rowId: RowId,
    _handler: (change: CellChange) => void,
  ): Unsubscribe {
    return () => {};
  }

  // ── Writes ──────────────────────────────────────────────────────────
  //
  // The `expectedEtag` parameter is accepted for interface conformance
  // but not sent: the routes take no `If-Match` header. See `Backend.Etag`.

  async writeCell(
    rowId: RowId,
    cell: CellEntry,
    _expectedEtag: Option<Etag>,
    requestId: Option<string>,
  ): Promise<void> {
    const debounceKey = cellWriteDebounceKey(rowId, cell.date, cell.id ?? null);
    const body = SheetsNetwork.CellWriteRequest.make({
      date: SemanticTime.fromPartialDate(cell.date),
      data: cellValueWire(cell.value),
      id: cell.id ?? null,
      endDate: null,
    });
    await this.#handOffCell("write-cell", rowId, debounceKey, body, requestId);
  }

  async clearCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    id: Option<CellId>,
    _expectedEtag: Option<Etag>,
  ): Promise<void> {
    // The cell endpoint treats `data: null` as a clear. `id`
    // disambiguates within `multi`-cardinality sources.
    const debounceKey = cellWriteDebounceKey(rowId, date, id ?? null);
    const body = SheetsNetwork.CellWriteRequest.make({ date: SemanticTime.fromPartialDate(date), data: null, id, endDate: null });
    await this.#handOffCell("clear-cell", rowId, debounceKey, body, null);
  }

  /**
   * Hand one cell write to the service-worker queue under `debounceKey`. A
   * caller that names the write (`requestId`) claims its verdict itself (the
   * SDK's durable cell writes): the hand-off is all this does. Otherwise, with
   * a verdict channel, the write is claimed under its own request id and its
   * refusal — reported to the host once (`CellVerdicts.refused`) — throws
   * `RowsHttpBackendRequestError`, so the writer takes its paint back.
   */
  async #handOffCell(
    step: "write-cell" | "clear-cell",
    rowId: RowId,
    debounceKey: string,
    body: SheetsNetwork.CellWriteRequest,
    requestId: Option<string>,
  ): Promise<void> {
    const send = async (id: Option<string>): Promise<void> => {
      await this.#api.debounce.post("/api/rows/{rowKey}/cells", {
        path: { rowKey: RowNetworkKey.of(rowId) },
        body,
        // The write's own id, when it has one: a refusal then names the
        // writes this execution carried, never another request for the key.
        options: id == null ? { key: debounceKey } : { key: debounceKey, requestId: id },
      }, CELL_WRITE_DEBOUNCE_MS);
    };
    const verdicts = this.#verdicts;
    if (requestId != null || verdicts == null) {
      await send(requestId);
      return;
    }
    const refusal = await verdicts.handOff(debounceKey, send);
    if (refusal == null) return;
    const error = new RowsHttpBackendRequestError({
      step,
      method: "POST",
      path: "/api/rows/{rowKey}/cells",
      failure: refusal.failure,
      reason: refusal.reason,
      details: refusal.details,
    });
    verdicts.refused(error);
    throw error;
  }

  async writeTimepointCell(
    rowId: RowId,
    dateRange: DateTime.Range<Utc>,
    data: Option<Json | CellValue>,
    replaceCellId: Option<CellId>,
    _expectedEtag: Option<Etag>,
  ): Promise<SheetsNetwork.CellWriteResponse> {
    // Wire boundary: the server reads `replaceCellId` out of the
    // payload, so fold it in HERE — above this layer it travels as a
    // typed parameter, never smuggled inside the payload. `cellValueWire`
    // is the encoder: a canonical value's `DateTime`s become ISO strings,
    // exactly as `JSON.stringify` writes them, and a canonical event's
    // `null` extras are left out, as the stored cell has them.
    const wire = cellValueWire(data);
    const wireData = replaceCellId != null
      ? { ...(Json.object(wire) ?? {}), replaceCellId }
      : wire;
    return nonnull(
      await sendChecked(
        "write-timepoint-cell",
        "POST",
        "/api/rows/{rowKey}/cells",
        () =>
          this.#api.postResult("/api/rows/{rowKey}/cells", {
            path: { rowKey: RowNetworkKey.of(rowId) },
            body: SheetsNetwork.CellWriteRequest.make({
              date: SemanticTime.fromDateTime(dateRange.start),
              endDate: SemanticTime.fromDateTime(dateRange.end),
              data: wireData,
              id: null,
            }),
          }),
        false,
      ),
      "RowsHttpBackend.writeTimepointCell: accepted write carries a body",
    );
  }

  async clearTimepointCell(
    rowId: RowId,
    cellId: CellId,
    _expectedEtag: Option<Etag>,
  ): Promise<SheetsNetwork.CellWriteResponse> {
    // `id` + `data: null` dispatches to the row's delete handler; the
    // caller reconciles the deleted custom cell by id.
    return nonnull(
      await sendChecked(
        "clear-timepoint-cell",
        "POST",
        "/api/rows/{rowKey}/cells",
        () =>
          this.#api.postResult("/api/rows/{rowKey}/cells", {
            path: { rowKey: RowNetworkKey.of(rowId) },
            body: SheetsNetwork.CellWriteRequest.make({ id: cellId, data: null, date: null, endDate: null }),
          }),
        false,
      ),
      "RowsHttpBackend.clearTimepointCell: accepted write carries a body",
    );
  }

  async addRow(
    row: RawRow,
    sheet: Option<SheetId>,
    _position: Option<number>,
    _expectedEtag: Option<Etag>,
    surface: Option<SurfaceKind>,
  ): Promise<Option<RowId>> {
    // `position` is ignored — the `/api/rows` POST endpoint
    // doesn't accept it. The caller can issue a separate
    // reorder request when needed.
    void _position;
    // Wire boundary: the server's NewRow `id` / `sheetId` / `parentId` are all
    // strict `uuid::Uuid`, so send BARE uuids — the client canonical forms
    // (`r_<hex>`, `ss_<hex>`) would fail serde and 422. `sheetId` links the row
    // to its sheet; `parentId` (when present) links it as a CHILD of its
    // source/alias parent (the symmetric `SheetRowEdge`). The server MINTS the
    // row id for sheet-linked rows and returns it in `CreatedRow` for the caller
    // to rekey its local row to.
    const body = SheetsNetwork.NewRow.make({
      id: wireUuid("addRow", "id", row.id, RowUuid.parse),
      type: row.type,
      sheetId: sheet != null ? wireUuid("addRow", "sheetId", sheet.toString(), SheetUuid.parse) : null,
      parentId: row.parentId != null ? wireUuid("addRow", "parentId", row.parentId, RowUuid.parse) : null,
      label: row.label ?? null,
      lookupKey: row.lookupKey ?? null,
      attributes: row.attributes ?? null,
      surface,
    });
    const created = await sendChecked(
      "add",
      "POST",
      "/api/rows",
      () => this.#api.postResult("/api/rows", { body }),
      false,
    );
    // Adopt the server-minted id (re-canonicalized to `r_<hex>`) so the caller
    // can rekey; `null` only when the accepted answer echoed no id.
    return created != null && created.id != null
      ? RowId.fromUuid(String(created.id))
      : null;
  }

  /**
   * Patch the row's own fields (`PATCH /api/rows/{rowKey}`): `label`, `icon`,
   * `formula`, `lookupKey`, `timeScale`, `heightPx`, `attributes` (`null`
   * clears one). Resolves with the server's answer (`shouldRefetchData`). A
   * patch that carries any other field (`parentId`, `children`, `cells`,
   * `meta`, `id`, `type`: each has its own route — reorder, the cell writes,
   * the metadata path) or a time scale the route does not name throws
   * `RowsHttpBackendRequestError` (step `update`, outcome `unaddressable`)
   * before anything is sent; a refused or unanswered PATCH throws it too.
   */
  async updateRow(
    rowId: RowId,
    patch: Backend.UpdateRowBody,
    _expectedEtag: Option<Etag>,
  ): Promise<Backend.RowUpdated> {
    const scale: Option<string> = patch.timeScale ?? null;
    const timeScale = scale != null ? wireTimescale(scale) : null;
    const unsent = Object.entries(patch)
      .filter(([field, value]) => value !== undefined && !UPDATE_ROW_FIELDS.has(field))
      .map(([field]) => field);
    if (scale != null && timeScale == null) unsent.push(`timeScale "${scale}"`);
    if (unsent.length > 0) {
      throw new RowsHttpBackendRequestError({
        step: "update",
        method: "PATCH",
        path: "/api/rows/{rowKey}",
        failure: { outcome: "unaddressable" },
        details: `the route takes ${[...UPDATE_ROW_FIELDS].join(", ")} (a time scale of ` +
          `${WIRE_TIMESCALES.join(" | ")}); not sent: ${unsent.join(", ")}`,
      });
    }
    const body: SheetsNetwork.PatchRow_PendingValidation = {
      ...(patch.label !== undefined ? { label: patch.label } : {}),
      ...(patch.formula !== undefined ? { formula: patch.formula } : {}),
      ...(patch.lookupKey !== undefined
        ? { lookupKey: patch.lookupKey }
        : {}),
      ...(timeScale != null ? { timeScale } : patch.timeScale === null ? { timeScale: null } : {}),
      ...(patch.heightPx !== undefined ? { heightPx: patch.heightPx } : {}),
      ...(patch.icon !== undefined ? { icon: patch.icon } : {}),
      ...(patch.attributes !== undefined
        ? { attributes: patch.attributes }
        : {}),
    };
    const answer = await sendChecked(
      "update",
      "PATCH",
      "/api/rows/{rowKey}",
      () =>
        this.#api.patchResult("/api/rows/{rowKey}", {
          path: { rowKey: RowNetworkKey.of(rowId) },
          body,
        }),
      false,
    );
    return { shouldRefetchData: answer?.shouldRefetchData === true };
  }

  /** Delete the row; throws `RowsHttpBackendRequestError` on a server failure. */
  async deleteRow(rowId: RowId, _expectedEtag: Option<Etag>): Promise<void> {
    await sendChecked(
      "delete",
      "DELETE",
      "/api/rows/{rowKey}",
      () => this.#api.delResult("/api/rows/{rowKey}", { path: { rowKey: RowNetworkKey.of(rowId) } }),
      false,
    );
  }
}

/** Types that go with `RowsHttpBackend`: `Step`. */
export namespace RowsHttpBackend {
  /**
   * The checked request that failed, named by the SDK-wide step lexicon
   * (`@tento/lona` `errors.ts`): `get` the sheet; `add` / `update` /
   * `delete` a row; `write-timepoint-cell` / `clear-timepoint-cell`.
   */
  export type Step =
    | "get"
    | "add"
    | "update"
    | "delete"
    | "write-cell"
    | "clear-cell"
    | "write-timepoint-cell"
    | "clear-timepoint-cell";

  /**
   * The verdict channel of a backend's handed-off cell writes (`writeCell` /
   * `clearCell`): the client's durable-write registry, which claims the
   * refusal of the execution that carried a write. `handOff` sends the write
   * through `send` under the write's own request id and answers its refusal
   * (`null`: the execution answered without refusing it); `refused` reports a
   * refused write to the host, once, before it rejects.
   */
  export interface CellVerdicts {
    /** Send one write through `send` under its own request id; answer its refusal (`null`: none). */
    handOff(key: string, send: (requestId: string) => Promise<void>): Promise<Option<CellRefusal>>;
    /** Report a refused write to the host, once, before it rejects. */
    refused(error: RowsHttpBackendRequestError): void;
  }

  /** What became of a refused cell write (`CellVerdicts.handOff`). */
  export type CellRefusal = {
    /** Refused with a status, unanswered, or answered with a body that was not the route's. */
    readonly failure: SdkRequestError.Answered;
    /** The server's category slug (`""`: none). */
    readonly reason: string;
    /** The server's words (`""`: none). */
    readonly details: string;
  };
}

/**
 * A checked `RowsHttpBackend` request the server did not accept: a non-2xx
 * answer (`status`), no answer at all (`status: null`), an answer whose
 * body could not be read (`malformed`), or a request never sent
 * (`unaddressable`: an `updateRow` patch carrying a field its route does
 * not take). `reason` / `details` carry the server's `ResponseError` fields
 * (`""` when absent), or what was not sent.
 * Thrown before any caller-visible change, so the caller only reports it.
 *
 * An `SdkRequestError` (boundary `"rows-http"`), so one
 * `instanceof SdkRequestError` covers it beside every `@tento/lona`
 * boundary error.
 */
export class RowsHttpBackendRequestError extends SdkRequestError {
  declare readonly boundary: "rows-http";
  declare readonly step: RowsHttpBackend.Step;
  declare readonly method: "GET" | "POST" | "PATCH" | "DELETE";

  constructor(init: RowsHttpBackendRequestError.Init) {
    super({ ...init, boundary: "rows-http" });
    this.name = "RowsHttpBackendRequestError";
  }
}

/** Types that go with `RowsHttpBackendRequestError`: `Init`. */
export namespace RowsHttpBackendRequestError {
  /** What the constructor takes: the request, and what became of it. */
  export type Init = SdkRequestError.SubclassInit<
    RowsHttpBackend.Step,
    "GET" | "POST" | "PATCH" | "DELETE"
  >;
}

/**
 * A tolerant cell read's own request failure — what its `#api.get` threw —
 * as the request's `SdkRequestError` (boundary `cells`): `"unanswered"` for
 * a rejected fetch, `"malformed"` for a body that did not decode; the thrown
 * value is its `cause`. An `SdkRequestError` is already the request's verdict
 * (a `RequestErrorHandler` such as `RequestErrorHandlers.THROW` rethrew the
 * refusal with its status) and is returned as is.
 */
function cellReadRequestError(step: string, path: string, rejected: unknown): SdkRequestError {
  const err = requestRejection(rejected);
  if (err instanceof SdkRequestError) return err;
  return SdkRequestError.rejected(err, (answer) =>
    new SdkRequestError({ boundary: "cells", step, method: "GET", path, ...SdkRequestError.Answer.fields(answer) }));
}

/**
 * Send one checked request and unwrap it. `Ok` resolves the body; a 404
 * resolves `null` when `notFoundIsNull`; any other `Err`, or a thrown
 * transport failure, throws `RowsHttpBackendRequestError`.
 */
async function sendChecked<T>(
  step: RowsHttpBackend.Step,
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  send: () => Promise<Result<T, ResponseError>>,
  notFoundIsNull: boolean,
): Promise<Option<T>> {
  let result: Result<T, ResponseError>;
  try {
    result = await send();
  } catch (err) {
    throw SdkRequestError.rejected(err, (answer) =>
      new RowsHttpBackendRequestError({ step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }
  const error = result.err();
  if (error == null) return result.ok() ?? null;
  if (notFoundIsNull && error.code === 404) return null;
  throw new RowsHttpBackendRequestError({
    step,
    method,
    path,
    failure: SdkRequestError.answered(error.code),
    reason: error.reason,
    details: error.details,
  });
}

/**
 * A client id as the wire's bare UUID, branded by `parse` (`RowUuid` /
 * `SheetUuid`). The server's `NewRow` ids are strict
 * `uuid::Uuid`, so a canonical `<prefix>_<32 hex>` form (`r_…`, `ss_…`) is
 * re-dashed and a bare uuid passes through; anything else (a pending /
 * template / lookup id) throws here rather than 422 at the server.
 */
function wireUuid<B>(
  where: string,
  field: string,
  id: string,
  parse: (raw: string) => Option<B>,
): B {
  const hex = /^[a-z]+_([0-9a-f]{32})$/i.exec(id)?.[1];
  const bare = hex != null
    ? `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    : id;
  const branded = UuidFormat.is(bare) ? parse(bare) : null;
  if (branded == null) {
    throw new Error(`RowsHttpBackend.${where}: ${field} "${id}" is not a uuid`);
  }
  return branded;
}

// ── SheetsNetwork → RawSheet translation ───────────────────────────────────

function toRawSheet(wire: SheetsNetwork.UserSheetWithRows): RawSheet {
  return {
    id: SheetNetworkKey.fromWire(String(wire.id)),
    ...(wire.title != null ? { title: wire.title } : {}),
    ...(wire.width != null ? { width: wire.width } : {}),
    accessRole: wire.role,
    rows: wire.rows.map(toRawRow),
  };
}

function toRawRow(wire: SheetsNetwork.SheetRow): RawRow {
  const raw: RawRow = {
    id: RowNetworkKey.fromWire(String(wire.id)),
    type: wire.type,
    ...(wire.label != null ? { label: wire.label } : {}),
    ...(wire.formula != null ? { formula: wire.formula } : {}),
    ...(wire.lookupKey != null ? { lookupKey: wire.lookupKey } : {}),
    ...(wire.timeScale != null ? { timeScale: wire.timeScale } : {}),
    ...(wire.heightPx != null ? { heightPx: wire.heightPx } : {}),
    ...(Json.object(wire.attributes) != null
      ? { attributes: Json.object(wire.attributes) ?? {} }
      : {}),
    ...(Json.object(wire.metadata) != null
      ? { meta: Json.object(wire.metadata) ?? {} }
      : {}),
  };
  return raw;
}

/** The fields `PATCH /api/rows/{rowKey}` takes (`updateRow`). */
const UPDATE_ROW_FIELDS: ReadonlySet<string> = new Set([
  "label",
  "icon",
  "formula",
  "lookupKey",
  "timeScale",
  "heightPx",
  "attributes",
]);

/** The time scales the row route names (`SheetsNetwork.TimeScaleCustom`). */
const WIRE_TIMESCALES: readonly SheetsNetwork.TimeScaleCustom[] = ["year", "month", "week", "day", "custom"];

/** `scale` when the wire names it (a `TimeScaleCustom`), else `null`. */
function wireTimescale(scale: string): Option<SheetsNetwork.TimeScaleCustom> {
  return WIRE_TIMESCALES.find((wire) => wire === scale) ?? null;
}
